import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { MessageSquare, AlertTriangle, Camera, X, Loader2, CheckCircle, Phone, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const translations = {
  ar: {
    centerName: "شركة مركز لوسنت للأسنان",
    pageTitle: "الاقتراحات والشكاوى",
    pageSubtitle: "نقدر ملاحظاتكم لتحسين خدماتنا",
    chooseType: "اختر نوع الملاحظة",
    suggestion: "اقتراح",
    suggestionDesc: "شاركنا أفكارك لتحسين الخدمة",
    complaint: "شكوى",
    complaintDesc: "أخبرنا عن أي مشكلة واجهتك",
    suggestionDetails: "تفاصيل الاقتراح",
    complaintDetails: "تفاصيل الشكوى",
    back: "→ رجوع",
    name: "الاسم",
    nameRequired: "الاسم مطلوب",
    namePlaceholder: "أدخل اسمك",
    phone: "رقم الجوال",
    phonePlaceholder: "5XXXXXXXX",
    phoneNote: "أدخل الرقم بدون صفر في البداية",
    suggestionContent: "تفاصيل الاقتراح",
    complaintContent: "تفاصيل الشكوى",
    suggestionPlaceholder: "اكتب اقتراحك هنا...",
    complaintPlaceholder: "اكتب تفاصيل شكواك هنا...",
    attachImage: "إرفاق صورة",
    optional: "(اختياري)",
    uploading: "جاري الرفع...",
    clickToAttach: "اضغط لإرفاق صورة",
    submitSuggestion: "إرسال الاقتراح",
    submitComplaint: "إرسال الشكوى",
    thankYou: "شكراً لك!",
    suggestionSuccess: "تم استلام اقتراحك وسيتم مراجعته من قبل الإدارة",
    complaintSuccess: "تم استلام شكواك وسيتم التعامل معها في أقرب وقت",
    sendAnother: "إرسال ملاحظة أخرى",
    required: "*",
    fillRequired: "يرجى تعبئة جميع الحقول المطلوبة",
    invalidPhone: "رقم الجوال غير صحيح",
    imageOnly: "يجب أن يكون الملف صورة",
    imageTooLarge: "حجم الصورة يجب أن لا يتجاوز 5 ميجابايت",
    imageUploaded: "تم رفع الصورة",
    imageUploadFailed: "فشل رفع الصورة",
    suggestionSent: "تم إرسال اقتراحك بنجاح",
    complaintSent: "تم إرسال شكواك بنجاح",
    submitError: "حدث خطأ أثناء الإرسال",
    footer: "© 2026 شركة مركز لوسنت للأسنان"
  },
  en: {
    centerName: "Lucent Dental Center",
    pageTitle: "Suggestions & Complaints",
    pageSubtitle: "We value your feedback to improve our services",
    chooseType: "Select Feedback Type",
    suggestion: "Suggestion",
    suggestionDesc: "Share your ideas to improve our service",
    complaint: "Complaint",
    complaintDesc: "Tell us about any issue you faced",
    suggestionDetails: "Suggestion Details",
    complaintDetails: "Complaint Details",
    back: "← Back",
    name: "Name",
    nameRequired: "Name is required",
    namePlaceholder: "Enter your name",
    phone: "Phone Number",
    phonePlaceholder: "5XXXXXXXX",
    phoneNote: "Enter number without leading zero",
    suggestionContent: "Suggestion Details",
    complaintContent: "Complaint Details",
    suggestionPlaceholder: "Write your suggestion here...",
    complaintPlaceholder: "Write your complaint details here...",
    attachImage: "Attach Image",
    optional: "(Optional)",
    uploading: "Uploading...",
    clickToAttach: "Click to attach an image",
    submitSuggestion: "Submit Suggestion",
    submitComplaint: "Submit Complaint",
    thankYou: "Thank You!",
    suggestionSuccess: "Your suggestion has been received and will be reviewed by management",
    complaintSuccess: "Your complaint has been received and will be handled as soon as possible",
    sendAnother: "Send Another Feedback",
    required: "*",
    fillRequired: "Please fill all required fields",
    invalidPhone: "Invalid phone number",
    imageOnly: "File must be an image",
    imageTooLarge: "Image size must not exceed 5MB",
    imageUploaded: "Image uploaded",
    imageUploadFailed: "Image upload failed",
    suggestionSent: "Your suggestion has been sent successfully",
    complaintSent: "Your complaint has been sent successfully",
    submitError: "An error occurred while submitting",
    footer: "© 2026 Lucent Dental Center"
  }
};

export default function PublicFeedback() {
  const [lang, setLang] = useState("ar");
  const [feedbackType, setFeedbackType] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    reporter_name: "",
    reporter_phone: "",
    content: "",
    images: []
  });

  const t = translations[lang];
  const isRTL = lang === "ar";

  const toggleLang = () => setLang(prev => prev === "ar" ? "en" : "ar");

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t.imageOnly);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t.imageTooLarge);
      return;
    }

    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/public/upload`, fd);
      setFormData(prev => ({ ...prev, images: [...prev.images, res.data.data] }));
      toast.success(t.imageUploaded);
    } catch {
      toast.error(t.imageUploadFailed);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.reporter_name.trim() || !formData.reporter_phone.trim() || !formData.content.trim()) {
      toast.error(t.fillRequired);
      return;
    }
    if (formData.reporter_phone.length < 8) {
      toast.error(t.invalidPhone);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        type: feedbackType,
        content: formData.content,
        reporter_name: formData.reporter_name,
        reporter_phone: `+966${formData.reporter_phone}`,
        images: formData.images
      };
      await axios.post(`${API}/public/feedback`, payload);
      setSubmitted(true);
      toast.success(feedbackType === "suggestion" ? t.suggestionSent : t.complaintSent);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.submitError);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFeedbackType(null);
    setSubmitted(false);
    setFormData({ reporter_name: "", reporter_phone: "", content: "", images: [] });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#655dc6] flex items-center justify-center p-4" dir={isRTL ? "rtl" : "ltr"}>
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="pt-10 pb-10 flex flex-col items-center gap-5 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#4a3fa8]">{t.thankYou}</h2>
            <p className="text-slate-600">
              {feedbackType === "suggestion" ? t.suggestionSuccess : t.complaintSuccess}
            </p>
            <Button onClick={resetForm} className="bg-[#655dc6] hover:bg-[#5449a8] text-white mt-2">
              {t.sendAnother}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#655dc6]" dir={isRTL ? "rtl" : "ltr"}>
      {/* Language Toggle */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLang}
          className="text-white/80 hover:text-white hover:bg-white/10 gap-1.5"
          data-testid="lang-toggle-btn"
        >
          <Globe className="w-4 h-4" />
          {lang === "ar" ? "English" : "العربية"}
        </Button>
      </div>

      {/* Header */}
      <div className="text-center py-6 text-white">
        <p className="text-sm opacity-80">{t.centerName}</p>
        <h1 className="text-xl font-bold mt-1">{t.pageTitle}</h1>
        <p className="text-xs opacity-60 mt-1">{t.pageSubtitle}</p>
      </div>

      <div className="max-w-lg mx-auto px-4 pb-10">
        {/* Type Selection */}
        {!feedbackType && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg text-[#4a3fa8] text-center">
                {t.chooseType}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={() => setFeedbackType("suggestion")}
                className={`w-full h-24 ${isRTL ? 'bg-gradient-to-l' : 'bg-gradient-to-r'} from-[#1565C0] to-[#42A5F5] hover:from-[#0D47A1] hover:to-[#1E88E5] text-white flex items-center justify-center gap-4`}
                data-testid="select-suggestion-btn"
              >
                <MessageSquare className="w-10 h-10" />
                <div className={isRTL ? "text-right" : "text-left"}>
                  <p className="text-xl font-bold">{t.suggestion}</p>
                  <p className="text-sm opacity-80">{t.suggestionDesc}</p>
                </div>
              </Button>

              <Button
                onClick={() => setFeedbackType("complaint")}
                className={`w-full h-24 ${isRTL ? 'bg-gradient-to-l' : 'bg-gradient-to-r'} from-[#C62828] to-[#EF5350] hover:from-[#B71C1C] hover:to-[#E53935] text-white flex items-center justify-center gap-4`}
                data-testid="select-complaint-btn"
              >
                <AlertTriangle className="w-10 h-10" />
                <div className={isRTL ? "text-right" : "text-left"}>
                  <p className="text-xl font-bold">{t.complaint}</p>
                  <p className="text-sm opacity-80">{t.complaintDesc}</p>
                </div>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Feedback Form */}
        {feedbackType && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className={`text-lg ${feedbackType === "suggestion" ? "text-[#1565C0]" : "text-[#C62828]"}`}>
                  {feedbackType === "suggestion" ? t.suggestionDetails : t.complaintDetails}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFeedbackType(null)}
                  className="text-slate-500"
                  data-testid="back-btn"
                >
                  {t.back}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Reporter Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t.name} <span className="text-red-500">{t.required}</span>
                    </label>
                    <Input
                      value={formData.reporter_name}
                      onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })}
                      placeholder={t.namePlaceholder}
                      required
                      data-testid="feedback-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t.phone} <span className="text-red-500">{t.required}</span>
                    </label>
                    <div className="flex gap-2 items-center" dir="ltr">
                      <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md px-3 h-10 text-sm font-medium text-slate-700 shrink-0">
                        <Phone className="w-3.5 h-3.5" />
                        <span>966+</span>
                      </div>
                      <Input
                        type="tel"
                        value={formData.reporter_phone}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          const cleaned = val.startsWith("0") ? val.slice(1) : val;
                          if (cleaned.length <= 9) {
                            setFormData({ ...formData, reporter_phone: cleaned });
                          }
                        }}
                        placeholder={t.phonePlaceholder}
                        required
                        className="text-left"
                        dir="ltr"
                        data-testid="feedback-phone"
                      />
                    </div>
                    <p className={`text-xs text-slate-400 mt-1 ${isRTL ? "text-right" : "text-left"}`}>{t.phoneNote}</p>
                  </div>
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {feedbackType === "suggestion" ? t.suggestionContent : t.complaintContent} <span className="text-red-500">{t.required}</span>
                  </label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder={feedbackType === "suggestion" ? t.suggestionPlaceholder : t.complaintPlaceholder}
                    rows={5}
                    required
                    data-testid="feedback-content"
                  />
                </div>

                {/* Image Upload (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t.attachImage} <span className="text-slate-400">{t.optional}</span>
                  </label>

                  {formData.images.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.images.map((img, idx) => (
                        <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                          <img src={img} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute top-0.5 left-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <label
                    className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${
                      uploadingImage ? "border-slate-300 bg-slate-50" : "border-slate-300 hover:border-[#655dc6] hover:bg-purple-50"
                    }`}
                  >
                    {uploadingImage ? (
                      <Loader2 className="w-5 h-5 animate-spin text-[#655dc6]" />
                    ) : (
                      <Camera className="w-5 h-5 text-slate-400" />
                    )}
                    <span className="text-sm text-slate-500">
                      {uploadingImage ? t.uploading : t.clickToAttach}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      data-testid="feedback-image-upload"
                    />
                  </label>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full h-12 text-white text-base font-bold ${
                    feedbackType === "suggestion"
                      ? "bg-[#1565C0] hover:bg-[#0D47A1]"
                      : "bg-[#C62828] hover:bg-[#B71C1C]"
                  }`}
                  data-testid="feedback-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    feedbackType === "suggestion" ? t.submitSuggestion : t.submitComplaint
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-4 text-white/50 text-xs">
        {t.footer}
      </div>
    </div>
  );
}
